import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { ArrowLeft, User, Mail, Phone, MapPin, DollarSign, MessageSquare, Clock, CheckCircle, Plus, Calendar, Trash2, TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CRMManagement() {
  const { token, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [contacts, setContacts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showFunnel, setShowFunnel] = useState(false);
  const [funnelStats, setFunnelStats] = useState(null);
  const [activeTab, setActiveTab] = useState('contacts'); // 'contacts', 'sales', 'reports'
  const [sales, setSales] = useState([]);
  const [financialStats, setFinancialStats] = useState(null);
  const [showSaleModal, setShowSaleModal] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [financialReport, setFinancialReport] = useState(null);
  const [saleData, setSaleData] = useState({
    contactId: '',
    propertyName: '',
    propertyAddress: '',
    saleValue: '',
    commissionPercent: '',
    commissionValue: '',
    saleDate: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [showClosedSaleModal, setShowClosedSaleModal] = useState(false);
  const [pendingClosedContact, setPendingClosedContact] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [taskData, setTaskData] = useState({
    title: '',
    description: '',
    dueDate: ''
  });

  useEffect(() => {
    if (!loading) {
      if (!token) {
        navigate('/admin/login');
      } else {
        loadContacts();
        if (activeTab === 'sales') {
          loadSales();
          loadFinancialStats();
        } else if (activeTab === 'reports') {
          loadFinancialReport();
        }
      }
    }
  }, [loading, token, navigate, activeTab]);

  useEffect(() => {
    if (activeTab === 'reports' && token) {
      loadFinancialReport();
    }
  }, [selectedYear, selectedMonth]);

  const loadContacts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/crm/contacts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContacts(response.data);
    } catch (error) {
      console.error('Erro ao carregar contactos:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível carregar os contactos"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewContact = async (contactId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/crm/contacts/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedContact(response.data);
      setShowContactModal(true);
    } catch (error) {
      console.error('Erro ao carregar contacto:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível carregar os detalhes do contacto"
      });
    }
  };

  const handleUpdateStatus = async (status) => {
    if (!selectedContact) return;

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/crm/contacts/${selectedContact.id}/status`,
        null,
        {
          params: { status },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast({
        title: "Sucesso",
        description: "Status atualizado"
      });
      setSelectedContact({ ...selectedContact, status });
      loadContacts();
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar o status"
      });
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !selectedContact) return;

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/crm/contacts/${selectedContact.id}/notes`,
        { text: noteText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: "Sucesso",
        description: "Nota adicionada"
      });
      setNoteText('');
      setShowNoteModal(false);
      handleViewContact(selectedContact.id); // Reload contact
    } catch (error) {
      console.error('Erro ao adicionar nota:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível adicionar a nota"
      });
    }
  };

  const handleAddTask = async () => {
    if (!taskData.title.trim() || !taskData.dueDate || !selectedContact) return;

    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/crm/contacts/${selectedContact.id}/tasks`,
        {
          title: taskData.title,
          description: taskData.description,
          dueDate: new Date(taskData.dueDate).toISOString()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: "Sucesso",
        description: "Tarefa criada"
      });
      setTaskData({ title: '', description: '', dueDate: '' });
      setShowTaskModal(false);
      handleViewContact(selectedContact.id); // Reload contact
    } catch (error) {
      console.error('Erro ao criar tarefa:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível criar a tarefa"
      });
    }
  };

  const handleToggleTask = async (taskId, completed) => {
    if (!selectedContact) return;

    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/crm/contacts/${selectedContact.id}/tasks/${taskId}`,
        null,
        {
          params: { completed },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      handleViewContact(selectedContact.id); // Reload contact
    } catch (error) {
      console.error('Erro ao atualizar tarefa:', error);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Tem certeza que deseja excluir este cliente? Esta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/crm/contacts/${contactId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: "Sucesso",
        description: "Cliente excluído com sucesso"
      });
      setShowContactModal(false);
      loadContacts();
    } catch (error) {
      console.error('Erro ao excluir cliente:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível excluir o cliente"
      });
    }
  };

  const handleUpdateStage = async (stage) => {
    if (!selectedContact) return;

    // Se for "fechado", abrir modal de venda obrigatório
    if (stage === 'fechado') {
      setPendingClosedContact(selectedContact);
      setSaleData({
        contactId: selectedContact.id,
        propertyName: '',
        propertyAddress: '',
        saleValue: '',
        commissionPercent: '',
        commissionValue: '',
        saleDate: new Date().toISOString().split('T')[0],
        notes: ''
      });
      setShowClosedSaleModal(true);
      return;
    }

    // Para outros stages, atualizar normalmente
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/crm/contacts/${selectedContact.id}/stage`,
        null,
        {
          params: { stage },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast({
        title: "Sucesso",
        description: "Etapa do funil atualizada"
      });
      setSelectedContact({ ...selectedContact, stage });
      loadContacts();
    } catch (error) {
      console.error('Erro ao atualizar etapa:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível atualizar a etapa"
      });
    }
  };

  const loadFunnelStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/crm/funnel/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFunnelStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas do funil:', error);
    }
  };

  const loadSales = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/crm/sales`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSales(response.data);
    } catch (error) {
      console.error('Erro ao carregar vendas:', error);
    }
  };

  const loadFinancialStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/crm/financial/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFinancialStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas financeiras:', error);
    }
  };

  const loadFinancialReport = async () => {
    try {
      const params = { year: selectedYear };
      if (selectedMonth) params.month = selectedMonth;
      
      const response = await axios.get(`${BACKEND_URL}/api/admin/crm/financial/report`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setFinancialReport(response.data);
    } catch (error) {
      console.error('Erro ao carregar relatório financeiro:', error);
    }
  };

  const handleCreateSale = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/crm/sales`, saleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Sucesso",
        description: "Venda registrada com sucesso"
      });
      
      setShowSaleModal(false);
      setSaleData({
        contactId: '',
        propertyName: '',
        propertyAddress: '',
        saleValue: '',
        commissionPercent: '',
        commissionValue: '',
        saleDate: new Date().toISOString().split('T')[0],
        notes: ''
      });
      loadSales();
      loadFinancialStats();
      loadContacts(); // Atualizar lista de contatos
    } catch (error) {
      console.error('Erro ao registrar venda:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível registrar a venda"
      });
    }
  };

  const handleClosedSaleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Criar a venda (isso automaticamente move o stage para "fechado" no backend)
      await axios.post(`${BACKEND_URL}/api/admin/crm/sales`, saleData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Sucesso",
        description: "Venda registrada e cliente movido para Fechado"
      });
      
      setShowClosedSaleModal(false);
      setPendingClosedContact(null);
      setSaleData({
        contactId: '',
        propertyName: '',
        propertyAddress: '',
        saleValue: '',
        commissionPercent: '',
        commissionValue: '',
        saleDate: new Date().toISOString().split('T')[0],
        notes: ''
      });
      
      // Atualizar contact selecionado
      if (selectedContact) {
        setSelectedContact({ ...selectedContact, stage: 'fechado' });
      }
      
      loadContacts();
      loadSales();
      loadFinancialStats();
    } catch (error) {
      console.error('Erro ao registrar venda:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível registrar a venda"
      });
    }
  };

  const handleDeleteSale = async (saleId) => {
    if (!window.confirm('Tem certeza que deseja excluir esta venda?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/crm/sales/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Sucesso",
        description: "Venda excluída com sucesso"
      });
      
      loadSales();
      loadFinancialStats();
    } catch (error) {
      console.error('Erro ao excluir venda:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Não foi possível excluir a venda"
      });
    }
  };

  const calculateCommission = (value, percent) => {
    return (parseFloat(value || 0) * parseFloat(percent || 0)) / 100;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const getStageBadge = (stage) => {
    const badges = {
      lead: { label: 'Lead', color: 'bg-gray-100 text-gray-800' },
      contato_inicial: { label: 'Contato Inicial', color: 'bg-blue-100 text-blue-800' },
      qualificado: { label: 'Qualificado', color: 'bg-cyan-100 text-cyan-800' },
      proposta: { label: 'Proposta Enviada', color: 'bg-purple-100 text-purple-800' },
      negociacao: { label: 'Em Negociação', color: 'bg-yellow-100 text-yellow-800' },
      fechado: { label: 'Fechado', color: 'bg-green-100 text-green-800' },
      perdido: { label: 'Perdido', color: 'bg-red-100 text-red-800' }
    };
    const badge = badges[stage] || badges.lead;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: { label: 'Novo', color: 'bg-blue-100 text-blue-800' },
      in_progress: { label: 'Em Progresso', color: 'bg-yellow-100 text-yellow-800' },
      completed: { label: 'Concluído', color: 'bg-green-100 text-green-800' },
      archived: { label: 'Arquivado', color: 'bg-gray-100 text-gray-800' }
    };
    const badge = badges[status] || badges.new;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  if (loading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="outline" 
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
              CRM - Gestão de Clientes
            </h1>
          </div>
          {activeTab === 'contacts' && (
            <Button
              onClick={() => {
                setShowFunnel(!showFunnel);
                if (!showFunnel && !funnelStats) loadFunnelStats();
              }}
              className="flex items-center gap-2"
            >
              <TrendingUp className="w-4 h-4" />
              {showFunnel ? 'Ocultar Funil' : 'Ver Funil de Vendas'}
            </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('contacts')}
            className={`px-4 py-2 font-semibold transition-colors ${
              activeTab === 'contacts'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Clientes
          </button>
          <button
            onClick={() => setActiveTab('sales')}
            className={`px-4 py-2 font-semibold transition-colors ${
              activeTab === 'sales'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Vendas
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`px-4 py-2 font-semibold transition-colors ${
              activeTab === 'reports'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Relatórios Financeiros
          </button>
        </div>

        {/* Sales Funnel */}
        {showFunnel && funnelStats && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Funil de Vendas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                <div className="text-center p-4 bg-gray-50 rounded-lg">
                  <div className="text-3xl font-bold text-gray-700">{funnelStats.lead || 0}</div>
                  <div className="text-sm text-gray-600 mt-1">Lead</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-3xl font-bold text-blue-700">{funnelStats.contato_inicial || 0}</div>
                  <div className="text-sm text-blue-600 mt-1">Contato Inicial</div>
                </div>
                <div className="text-center p-4 bg-cyan-50 rounded-lg">
                  <div className="text-3xl font-bold text-cyan-700">{funnelStats.qualificado || 0}</div>
                  <div className="text-sm text-cyan-600 mt-1">Qualificado</div>
                </div>
                <div className="text-center p-4 bg-purple-50 rounded-lg">
                  <div className="text-3xl font-bold text-purple-700">{funnelStats.proposta || 0}</div>
                  <div className="text-sm text-purple-600 mt-1">Proposta</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-3xl font-bold text-yellow-700">{funnelStats.negociacao || 0}</div>
                  <div className="text-sm text-yellow-600 mt-1">Negociação</div>
                </div>
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-3xl font-bold text-green-700">{funnelStats.fechado || 0}</div>
                  <div className="text-sm text-green-600 mt-1">Fechado ✓</div>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <div className="text-3xl font-bold text-red-700">{funnelStats.perdido || 0}</div>
                  <div className="text-sm text-red-600 mt-1">Perdido</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contacts List */}
        {activeTab === 'contacts' && (
        <div className="grid grid-cols-1 gap-4">
          {contacts.map((contact) => (
            <Card 
              key={contact.id} 
              className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => handleViewContact(contact.id)}
            >
              <CardContent className="p-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <User className="w-5 h-5 text-gray-500" />
                      <h3 className="text-lg font-semibold">{contact.name}</h3>
                      {getStatusBadge(contact.status || 'new')}
                      {getStageBadge(contact.stage || 'lead')}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4" />
                        <span>{contact.email}</span>
                      </div>
                      {contact.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <span>{contact.phone}</span>
                        </div>
                      )}
                      {contact.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{contact.location}</span>
                        </div>
                      )}
                      {contact.priceRange && (
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          <span>{contact.priceRange}</span>
                        </div>
                      )}
                    </div>
                    {contact.observations && (
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                        {contact.observations}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>{new Date(contact.createdAt).toLocaleDateString('pt-PT')}</span>
                    </div>
                    {contact.notes && contact.notes.length > 0 && (
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        <span>{contact.notes.length} nota(s)</span>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {contacts.length === 0 && !isLoading && (
            <div className="text-center py-12">
              <p className="text-gray-500">Nenhum contacto encontrado</p>
            </div>
          )}
        </div>
        )}

        {/* Sales Tab */}
        {activeTab === 'sales' && (
          <div className="space-y-6">
            {/* Financial Stats */}
            {financialStats && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Mês Atual</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="text-2xl font-bold">{financialStats.currentMonth.sales} vendas</div>
                      <div className="text-sm text-gray-600">Valor: {formatCurrency(financialStats.currentMonth.value)}</div>
                      <div className="text-sm font-semibold text-green-600">Comissão: {formatCurrency(financialStats.currentMonth.commission)}</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Ano Atual</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="text-2xl font-bold">{financialStats.currentYear.sales} vendas</div>
                      <div className="text-sm text-gray-600">Valor: {formatCurrency(financialStats.currentYear.value)}</div>
                      <div className="text-sm font-semibold text-green-600">Comissão: {formatCurrency(financialStats.currentYear.commission)}</div>
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Total Histórico</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="text-2xl font-bold">{financialStats.allTime.sales} vendas</div>
                      <div className="text-sm text-gray-600">Valor: {formatCurrency(financialStats.allTime.value)}</div>
                      <div className="text-sm font-semibold text-green-600">Comissão: {formatCurrency(financialStats.allTime.commission)}</div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Lista de Vendas</h2>
              <Button onClick={() => setShowSaleModal(true)} className="flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Nova Venda
              </Button>
            </div>

            {/* Sales List */}
            <div className="space-y-4">
              {sales.map((sale) => (
                <Card key={sale.id}>
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold mb-2">{sale.propertyName}</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div><strong>Cliente:</strong> {sale.contactName}</div>
                          <div><strong>Data:</strong> {new Date(sale.saleDate).toLocaleDateString('pt-PT')}</div>
                          <div><strong>Valor:</strong> {formatCurrency(sale.saleValue)}</div>
                          <div><strong>Comissão:</strong> {formatCurrency(sale.commissionValue)} ({sale.commissionPercent}%)</div>
                          {sale.propertyAddress && <div><strong>Endereço:</strong> {sale.propertyAddress}</div>}
                        </div>
                        {sale.notes && (
                          <p className="mt-2 text-sm text-gray-600">{sale.notes}</p>
                        )}
                      </div>
                      <Button variant="destructive" size="sm" onClick={() => handleDeleteSale(sale.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {sales.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-gray-500">Nenhuma venda registrada</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <div className="flex gap-4 items-center mb-6">
              <div>
                <Label>Ano</Label>
                <Input
                  type="number"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="w-32"
                />
              </div>
              <div>
                <Label>Mês (opcional)</Label>
                <select
                  value={selectedMonth || ''}
                  onChange={(e) => setSelectedMonth(e.target.value ? parseInt(e.target.value) : null)}
                  className="border rounded px-3 py-2"
                >
                  <option value="">Anual</option>
                  {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                    <option key={m} value={m}>{new Date(2000, m-1).toLocaleString('pt-PT', {month: 'long'})}</option>
                  ))}
                </select>
              </div>
            </div>

            {financialReport && (
              <Card>
                <CardHeader>
                  <CardTitle>Relatório Financeiro - {financialReport.period}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <div className="text-3xl font-bold text-blue-700">{financialReport.totalSales}</div>
                      <div className="text-sm text-blue-600">Total de Vendas</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-700">{formatCurrency(financialReport.totalValue)}</div>
                      <div className="text-sm text-green-600">Valor Total</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                      <div className="text-2xl font-bold text-purple-700">{formatCurrency(financialReport.totalCommission)}</div>
                      <div className="text-sm text-purple-600">Comissão Total</div>
                    </div>
                  </div>

                  {financialReport.type === 'yearly' && financialReport.monthlyBreakdown && (
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold mb-4">Breakdown Mensal</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="p-2 text-left">Mês</th>
                              <th className="p-2 text-right">Vendas</th>
                              <th className="p-2 text-right">Valor</th>
                              <th className="p-2 text-right">Comissão</th>
                            </tr>
                          </thead>
                          <tbody>
                            {financialReport.monthlyBreakdown.map((month) => (
                              <tr key={month.month} className="border-b">
                                <td className="p-2">{month.monthName}</td>
                                <td className="p-2 text-right">{month.sales}</td>
                                <td className="p-2 text-right">{formatCurrency(month.value)}</td>
                                <td className="p-2 text-right font-semibold text-green-600">{formatCurrency(month.commission)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {financialReport.type === 'monthly' && financialReport.sales && (
                    <div className="mt-6">
                      <h3 className="text-lg font-semibold mb-4">Vendas do Mês</h3>
                      <div className="space-y-2">
                        {financialReport.sales.map((sale) => (
                          <div key={sale.id} className="p-3 bg-gray-50 rounded flex justify-between">
                            <div>
                              <div className="font-semibold">{sale.propertyName}</div>
                              <div className="text-sm text-gray-600">{new Date(sale.saleDate).toLocaleDateString('pt-PT')}</div>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold">{formatCurrency(sale.saleValue)}</div>
                              <div className="text-sm text-green-600">{formatCurrency(sale.commissionValue)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Contact Detail Modal */}
        <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedContact && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-2xl">Detalhes do Cliente</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6">
                  {/* Client Info */}
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      {selectedContact.name}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div><strong>Email:</strong> {selectedContact.email}</div>
                      {selectedContact.phone && <div><strong>Telefone:</strong> {selectedContact.phone}</div>}
                      {selectedContact.location && <div><strong>Localização:</strong> {selectedContact.location}</div>}
                      {selectedContact.priceRange && <div><strong>Orçamento:</strong> {selectedContact.priceRange}</div>}
                    </div>
                    {selectedContact.observations && (
                      <div className="mt-3">
                        <strong>Observações:</strong>
                        <p className="text-sm text-gray-600 mt-1">{selectedContact.observations}</p>
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <h4 className="font-semibold mb-2">Status</h4>
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        size="sm" 
                        variant={selectedContact.status === 'new' ? 'default' : 'outline'}
                        onClick={() => handleUpdateStatus('new')}
                      >
                        Novo
                      </Button>
                      <Button 
                        size="sm" 
                        variant={selectedContact.status === 'in_progress' ? 'default' : 'outline'}
                        onClick={() => handleUpdateStatus('in_progress')}
                      >
                        Em Progresso
                      </Button>
                      <Button 
                        size="sm" 
                        variant={selectedContact.status === 'completed' ? 'default' : 'outline'}
                        onClick={() => handleUpdateStatus('completed')}
                      >
                        Concluído
                      </Button>
                      <Button 
                        size="sm" 
                        variant={selectedContact.status === 'archived' ? 'default' : 'outline'}
                        onClick={() => handleUpdateStatus('archived')}
                      >
                        Arquivado
                      </Button>
                    </div>
                  </div>

                  {/* Sales Funnel Stage */}
                  <div>
                    <h4 className="font-semibold mb-2 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5" />
                      Etapa do Funil de Vendas
                    </h4>
                    <div className="flex gap-2 flex-wrap">
                      <Button size="sm" variant={selectedContact.stage === 'lead' ? 'default' : 'outline'} onClick={() => handleUpdateStage('lead')}>
                        Lead
                      </Button>
                      <Button size="sm" variant={selectedContact.stage === 'contato_inicial' ? 'default' : 'outline'} onClick={() => handleUpdateStage('contato_inicial')}>
                        Contato Inicial
                      </Button>
                      <Button size="sm" variant={selectedContact.stage === 'qualificado' ? 'default' : 'outline'} onClick={() => handleUpdateStage('qualificado')}>
                        Qualificado
                      </Button>
                      <Button size="sm" variant={selectedContact.stage === 'proposta' ? 'default' : 'outline'} onClick={() => handleUpdateStage('proposta')}>
                        Proposta
                      </Button>
                      <Button size="sm" variant={selectedContact.stage === 'negociacao' ? 'default' : 'outline'} onClick={() => handleUpdateStage('negociacao')}>
                        Negociação
                      </Button>
                      <Button size="sm" variant={selectedContact.stage === 'fechado' ? 'default' : 'outline'} onClick={() => handleUpdateStage('fechado')}>
                        Fechado ✓
                      </Button>
                      <Button size="sm" variant={selectedContact.stage === 'perdido' ? 'outline' : 'outline'} onClick={() => handleUpdateStage('perdido')}>
                        Perdido
                      </Button>
                    </div>
                  </div>

                  {/* Delete Button */}
                  <div className="pt-4 border-t">
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleDeleteContact(selectedContact.id)}
                      className="flex items-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Excluir Cliente
                    </Button>
                  </div>

                  {/* Notes History */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <MessageSquare className="w-5 h-5" />
                        Histórico de Atendimento
                      </h4>
                      <Button size="sm" onClick={() => setShowNoteModal(true)}>
                        <Plus className="w-4 h-4 mr-1" />
                        Nova Nota
                      </Button>
                    </div>
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {selectedContact.notes && selectedContact.notes.length > 0 ? (
                        selectedContact.notes.map((note, index) => (
                          <div key={index} className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm">{note.text}</p>
                            <div className="mt-2 text-xs text-gray-500 flex justify-between">
                              <span>Por: {note.createdBy}</span>
                              <span>{new Date(note.createdAt).toLocaleString('pt-PT')}</span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">Nenhuma nota registada</p>
                      )}
                    </div>
                  </div>

                  {/* Tasks */}
                  <div>
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="font-semibold flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        Tarefas
                      </h4>
                      <Button size="sm" onClick={() => setShowTaskModal(true)}>
                        <Plus className="w-4 h-4 mr-1" />
                        Nova Tarefa
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {selectedContact.tasks && selectedContact.tasks.length > 0 ? (
                        selectedContact.tasks.map((task) => (
                          <div key={task.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                            <input
                              type="checkbox"
                              checked={task.completed || false}
                              onChange={(e) => handleToggleTask(task.id, e.target.checked)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <p className={`text-sm font-medium ${task.completed ? 'line-through text-gray-500' : ''}`}>
                                {task.title}
                              </p>
                              {task.description && (
                                <p className="text-xs text-gray-600 mt-1">{task.description}</p>
                              )}
                              <p className="text-xs text-gray-500 mt-1">
                                Vencimento: {new Date(task.dueDate).toLocaleDateString('pt-PT')}
                              </p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500">Nenhuma tarefa criada</p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Note Modal */}
        <Dialog open={showNoteModal} onOpenChange={setShowNoteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar Nota</DialogTitle>
            </DialogHeader>
            <div>
              <Label htmlFor="note">Nota</Label>
              <Textarea
                id="note"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Descreva o atendimento, conversas ou observações..."
                rows={4}
                className="mt-2"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowNoteModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddNote} className="bg-[#2E5E99] hover:bg-[#0D2440]">
                Adicionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Task Modal */}
        <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nova Tarefa</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="taskTitle">Título</Label>
                <Input
                  id="taskTitle"
                  value={taskData.title}
                  onChange={(e) => setTaskData({ ...taskData, title: e.target.value })}
                  placeholder="Ex: Ligar ao cliente"
                />
              </div>
              <div>
                <Label htmlFor="taskDesc">Descrição (opcional)</Label>
                <Textarea
                  id="taskDesc"
                  value={taskData.description}
                  onChange={(e) => setTaskData({ ...taskData, description: e.target.value })}
                  placeholder="Detalhes adicionais..."
                  rows={3}
                />
              </div>
              <div>
                <Label htmlFor="taskDate">Data de Vencimento</Label>
                <Input
                  id="taskDate"
                  type="date"
                  value={taskData.dueDate}
                  onChange={(e) => setTaskData({ ...taskData, dueDate: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTaskModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleAddTask} className="bg-[#2E5E99] hover:bg-[#0D2440]">
                Criar Tarefa
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Sale Modal */}
        <Dialog open={showSaleModal} onOpenChange={setShowSaleModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Registrar Nova Venda</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateSale}>
              <div className="space-y-4">
                <div>
                  <Label>Cliente*</Label>
                  <select
                    required
                    value={saleData.contactId}
                    onChange={(e) => setSaleData({ ...saleData, contactId: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="">Selecione um cliente</option>
                    {contacts.map(c => (
                      <option key={c.id} value={c.id}>{c.name} - {c.email}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Nome do Imóvel*</Label>
                  <Input
                    required
                    value={saleData.propertyName}
                    onChange={(e) => setSaleData({ ...saleData, propertyName: e.target.value })}
                    placeholder="Ex: Apartamento T2 na Av. da Liberdade"
                  />
                </div>

                <div>
                  <Label>Endereço do Imóvel</Label>
                  <Input
                    value={saleData.propertyAddress}
                    onChange={(e) => setSaleData({ ...saleData, propertyAddress: e.target.value })}
                    placeholder="Ex: Av. da Liberdade, 123, Lisboa"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Valor da Venda (€)*</Label>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      value={saleData.saleValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSaleData({ 
                          ...saleData, 
                          saleValue: value,
                          commissionValue: calculateCommission(value, saleData.commissionPercent)
                        });
                      }}
                      placeholder="500000"
                    />
                  </div>

                  <div>
                    <Label>Percentagem de Comissão (%)*</Label>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      value={saleData.commissionPercent}
                      onChange={(e) => {
                        const percent = e.target.value;
                        setSaleData({ 
                          ...saleData, 
                          commissionPercent: percent,
                          commissionValue: calculateCommission(saleData.saleValue, percent)
                        });
                      }}
                      placeholder="3"
                    />
                  </div>
                </div>

                <div>
                  <Label>Valor da Comissão (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={saleData.commissionValue}
                    onChange={(e) => setSaleData({ ...saleData, commissionValue: e.target.value })}
                    placeholder="Calculado automaticamente"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {saleData.saleValue && saleData.commissionPercent ? 
                      `Comissão calculada: ${formatCurrency(calculateCommission(saleData.saleValue, saleData.commissionPercent))}` 
                      : 'Preencha valor e percentagem'}
                  </p>
                </div>

                <div>
                  <Label>Data da Venda*</Label>
                  <Input
                    required
                    type="date"
                    value={saleData.saleDate}
                    onChange={(e) => setSaleData({ ...saleData, saleDate: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Notas</Label>
                  <Textarea
                    value={saleData.notes}
                    onChange={(e) => setSaleData({ ...saleData, notes: e.target.value })}
                    placeholder="Informações adicionais sobre a venda..."
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button variant="outline" type="button" onClick={() => setShowSaleModal(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Registrar Venda
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Closed Sale Modal (Obrigatório quando mudar para Fechado) */}
        <Dialog open={showClosedSaleModal} onOpenChange={(open) => {
          // Bloquear fechamento do modal - só permitir via botões
          if (!open) {
            toast({
              variant: "destructive",
              title: "Venda Obrigatória",
              description: "Para mover para 'Fechado', você precisa registrar os dados da venda ou clicar em Cancelar"
            });
            return false;
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-green-700">
                ✓ Registrar Venda - Cliente: {pendingClosedContact?.name}
              </DialogTitle>
              <p className="text-sm text-gray-600">
                Para mover este cliente para "Fechado", é obrigatório registrar os dados da venda
              </p>
            </DialogHeader>
            <form onSubmit={handleClosedSaleSubmit}>
              <div className="space-y-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm"><strong>Cliente:</strong> {pendingClosedContact?.name}</p>
                  <p className="text-sm"><strong>Email:</strong> {pendingClosedContact?.email}</p>
                  {pendingClosedContact?.phone && (
                    <p className="text-sm"><strong>Telefone:</strong> {pendingClosedContact.phone}</p>
                  )}
                </div>

                <div>
                  <Label className="text-red-600">Nome do Condomínio / Imóvel*</Label>
                  <Input
                    required
                    value={saleData.propertyName}
                    onChange={(e) => setSaleData({ ...saleData, propertyName: e.target.value })}
                    placeholder="Ex: Condomínio Liberdade Residence - Apartamento T2"
                  />
                </div>

                <div>
                  <Label className="text-red-600">Endereço Completo do Imóvel*</Label>
                  <Input
                    required
                    value={saleData.propertyAddress}
                    onChange={(e) => setSaleData({ ...saleData, propertyAddress: e.target.value })}
                    placeholder="Ex: Av. da Liberdade, 123, 3º Esq., 1250-096 Lisboa"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-red-600">Valor da Venda (€)*</Label>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      min="0"
                      value={saleData.saleValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSaleData({ 
                          ...saleData, 
                          saleValue: value,
                          commissionValue: calculateCommission(value, saleData.commissionPercent)
                        });
                      }}
                      placeholder="500000"
                    />
                  </div>

                  <div>
                    <Label className="text-red-600">Percentagem de Comissão (%)*</Label>
                    <Input
                      required
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      value={saleData.commissionPercent}
                      onChange={(e) => {
                        const percent = e.target.value;
                        setSaleData({ 
                          ...saleData, 
                          commissionPercent: percent,
                          commissionValue: calculateCommission(saleData.saleValue, percent)
                        });
                      }}
                      placeholder="3"
                    />
                  </div>
                </div>

                <div className="bg-green-50 p-3 rounded-lg">
                  <Label>Valor da Comissão (calculado automaticamente)</Label>
                  <div className="text-2xl font-bold text-green-700 mt-1">
                    {saleData.saleValue && saleData.commissionPercent ? 
                      formatCurrency(calculateCommission(saleData.saleValue, saleData.commissionPercent)) 
                      : '€0,00'}
                  </div>
                </div>

                <div>
                  <Label>Data da Venda*</Label>
                  <Input
                    required
                    type="date"
                    value={saleData.saleDate}
                    onChange={(e) => setSaleData({ ...saleData, saleDate: e.target.value })}
                  />
                </div>

                <div>
                  <Label>Notas / Observações</Label>
                  <Textarea
                    value={saleData.notes}
                    onChange={(e) => setSaleData({ ...saleData, notes: e.target.value })}
                    placeholder="Informações adicionais sobre a venda, condições especiais, etc."
                    rows={3}
                  />
                </div>
              </div>

              <DialogFooter className="mt-6">
                <Button 
                  variant="outline" 
                  type="button" 
                  onClick={() => {
                    setShowClosedSaleModal(false);
                    setPendingClosedContact(null);
                  }}
                >
                  Cancelar (não mover para Fechado)
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  ✓ Confirmar Venda e Mover para Fechado
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
